import { useState } from 'react';
import type { DocItem, DocFile } from '@/mocks/documents';

interface DocEditorProps {
  title: string;
  items: DocItem[];
  storageKey: string;
  onUpdate: (items: DocItem[]) => void;
}

export default function DocEditor({ title, items, storageKey, onUpdate }: DocEditorProps) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<DocItem | null>(null);
  const [addingNew, setAddingNew] = useState(false);
  const [newItem, setNewItem] = useState<DocItem>({ id: '', title: '', files: [{ label: '', url: '' }] });
  const [saved, setSaved] = useState(false);

  const save = (updated: DocItem[]) => {
    onUpdate(updated);
    localStorage.setItem(storageKey, JSON.stringify(updated));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const deleteItem = (id: string) => {
    if (!window.confirm('Удалить документ?')) return;
    save(items.filter((i) => i.id !== id));
  };

  const startEdit = (item: DocItem) => {
    setEditingItem(JSON.parse(JSON.stringify(item)));
    setExpanded(item.id);
    setAddingNew(false);
  };

  const saveEdit = () => {
    if (!editingItem) return;
    save(items.map((i) => (i.id === editingItem.id ? editingItem : i)));
    setEditingItem(null);
  };

  const updateEditFile = (idx: number, field: keyof DocFile, value: string) => {
    if (!editingItem) return;
    const files = [...editingItem.files];
    files[idx] = { ...files[idx], [field]: value };
    setEditingItem({ ...editingItem, files });
  };

  const addEditFile = () => {
    if (!editingItem) return;
    setEditingItem({ ...editingItem, files: [...editingItem.files, { label: '', url: '' }] });
  };

  const removeEditFile = (idx: number) => {
    if (!editingItem) return;
    setEditingItem({ ...editingItem, files: editingItem.files.filter((_, i) => i !== idx) });
  };

  const updateNewFile = (idx: number, field: keyof DocFile, value: string) => {
    const files = [...newItem.files];
    files[idx] = { ...files[idx], [field]: value };
    setNewItem({ ...newItem, files });
  };

  const saveNew = () => {
    if (!newItem.title.trim()) return;
    const item: DocItem = {
      ...newItem,
      id: `doc-${Date.now()}`,
      files: newItem.files.filter((f) => f.label.trim() || f.url.trim()),
    };
    save([...items, item]);
    setNewItem({ id: '', title: '', files: [{ label: '', url: '' }] });
    setAddingNew(false);
  };

  return (
    <div className="bg-background-900/60 rounded-xl border border-background-700/40 overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-background-800/40">
        <h3 className="font-heading text-foreground-200 text-sm tracking-widest uppercase">{title}</h3>
        <div className="flex items-center gap-2">
          {saved && (
            <span className="text-xs text-green-400 font-label tracking-wide">
              <i className="ri-check-line mr-1" />Сохранено
            </span>
          )}
          <button
            onClick={() => { setAddingNew(true); setEditingItem(null); }}
            className="flex items-center gap-2 px-3 py-1.5 bg-accent-500/20 hover:bg-accent-500/30 border border-accent-500/30 rounded text-xs text-accent-400 font-label tracking-wide transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line" /> Добавить
          </button>
        </div>
      </div>

      <div className="p-4 space-y-2 max-h-[500px] overflow-y-auto">
        {items.map((item) => {
          const isEditing = editingItem?.id === item.id;
          const isOpen = expanded === item.id;

          return (
            <div key={item.id} className="border border-background-700/40 rounded-lg overflow-hidden">
              <div className="flex items-start gap-3 px-4 py-3 bg-background-800/40">
                <button
                  onClick={() => setExpanded(isOpen ? null : item.id)}
                  className="mt-0.5 w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-500 hover:text-foreground-300 cursor-pointer"
                >
                  <i className={`ri-${isOpen ? 'subtract-line' : 'add-line'} text-sm`} />
                </button>
                <p className="flex-1 text-xs text-foreground-400 leading-relaxed line-clamp-2">{item.title}</p>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => startEdit(item)}
                    className="w-7 h-7 flex items-center justify-center text-foreground-500 hover:text-accent-400 transition-colors cursor-pointer"
                    title="Редактировать"
                  >
                    <i className="ri-edit-line text-sm" />
                  </button>
                  <button
                    onClick={() => deleteItem(item.id)}
                    className="w-7 h-7 flex items-center justify-center text-foreground-500 hover:text-red-400 transition-colors cursor-pointer"
                    title="Удалить"
                  >
                    <i className="ri-delete-bin-line text-sm" />
                  </button>
                </div>
              </div>

              {isOpen && !isEditing && (
                <div className="px-4 py-3 border-t border-background-700/30 bg-background-900/40">
                  <ul className="space-y-1">
                    {item.files.map((f, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-xs">
                        <i className="ri-file-pdf-line text-accent-500 text-sm flex-shrink-0" />
                        <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-accent-400 hover:text-accent-300 underline truncate">{f.label}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {isEditing && editingItem && (
                <div className="px-4 py-4 border-t border-background-700/30 bg-background-950/60 space-y-4">
                  <div>
                    <label className="block text-xs text-foreground-500 font-label tracking-wide mb-1">Заголовок документа</label>
                    <textarea
                      value={editingItem.title}
                      onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })}
                      rows={3}
                      className="w-full bg-background-900 border border-background-700 rounded px-3 py-2 text-xs text-foreground-300 focus:outline-none focus:border-accent-500 resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-foreground-500 font-label tracking-wide mb-2">Файлы</label>
                    <div className="space-y-2">
                      {editingItem.files.map((f, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <input
                            value={f.label}
                            onChange={(e) => updateEditFile(idx, 'label', e.target.value)}
                            placeholder="Название"
                            className="flex-1 bg-background-900 border border-background-700 rounded px-2 py-1.5 text-xs text-foreground-300 focus:outline-none focus:border-accent-500"
                          />
                          <input
                            value={f.url}
                            onChange={(e) => updateEditFile(idx, 'url', e.target.value)}
                            placeholder="URL (.pdf)"
                            className="flex-1 bg-background-900 border border-background-700 rounded px-2 py-1.5 text-xs text-foreground-300 focus:outline-none focus:border-accent-500"
                          />
                          <button
                            onClick={() => removeEditFile(idx)}
                            className="w-6 h-6 flex items-center justify-center text-foreground-600 hover:text-red-400 cursor-pointer"
                          >
                            <i className="ri-close-line text-sm" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={addEditFile}
                      className="mt-2 text-xs text-accent-500 hover:text-accent-400 font-label cursor-pointer"
                    >
                      + Добавить файл
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={saveEdit}
                      className="px-4 py-1.5 bg-accent-500 hover:bg-accent-600 text-background-950 text-xs font-label rounded cursor-pointer whitespace-nowrap"
                    >
                      Сохранить
                    </button>
                    <button
                      onClick={() => setEditingItem(null)}
                      className="px-4 py-1.5 border border-background-600 text-foreground-500 hover:text-foreground-300 text-xs font-label rounded cursor-pointer whitespace-nowrap"
                    >
                      Отмена
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {addingNew && (
          <div className="border border-accent-500/30 rounded-lg px-4 py-4 bg-background-900/60 space-y-4">
            <p className="text-xs font-label tracking-wide text-accent-400 uppercase">Новый документ</p>
            <div>
              <label className="block text-xs text-foreground-500 font-label tracking-wide mb-1">Заголовок</label>
              <textarea
                value={newItem.title}
                onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                rows={3}
                className="w-full bg-background-900 border border-background-700 rounded px-3 py-2 text-xs text-foreground-300 focus:outline-none focus:border-accent-500 resize-none"
                placeholder="Введите заголовок документа..."
              />
            </div>
            <div>
              <label className="block text-xs text-foreground-500 font-label tracking-wide mb-2">Файлы</label>
              <div className="space-y-2">
                {newItem.files.map((f, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      value={f.label}
                      onChange={(e) => updateNewFile(idx, 'label', e.target.value)}
                      placeholder="Название"
                      className="flex-1 bg-background-900 border border-background-700 rounded px-2 py-1.5 text-xs text-foreground-300 focus:outline-none focus:border-accent-500"
                    />
                    <input
                      value={f.url}
                      onChange={(e) => updateNewFile(idx, 'url', e.target.value)}
                      placeholder="URL (.pdf)"
                      className="flex-1 bg-background-900 border border-background-700 rounded px-2 py-1.5 text-xs text-foreground-300 focus:outline-none focus:border-accent-500"
                    />
                  </div>
                ))}
              </div>
              <button
                onClick={() => setNewItem({ ...newItem, files: [...newItem.files, { label: '', url: '' }] })}
                className="mt-2 text-xs text-accent-500 hover:text-accent-400 font-label cursor-pointer"
              >
                + Добавить файл
              </button>
            </div>
            <div className="flex gap-2">
              <button
                onClick={saveNew}
                disabled={!newItem.title.trim()}
                className="px-4 py-1.5 bg-accent-500 hover:bg-accent-600 text-background-950 text-xs font-label rounded cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                Добавить
              </button>
              <button
                onClick={() => setAddingNew(false)}
                className="px-4 py-1.5 border border-background-600 text-foreground-500 hover:text-foreground-300 text-xs font-label rounded cursor-pointer whitespace-nowrap"
              >
                Отмена
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}